import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Shield, 
  Lock, 
  Award,
  CheckCircle,
  XCircle,
  AlertTriangle,
  BookOpen,
  Target,
  Scale
} from "lucide-react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";

const certificationLevels = [
  {
    tier: "Foundation",
    status: "locked",
    description: "Core authority principles and operating standards",
    modules: "Modules 1–4",
    requirements: ["Application approved", "Initial assessment passed"],
  },
  {
    tier: "Operator",
    status: "locked",
    description: "Advanced systems and licensed execution authority",
    modules: "Modules 5–8",
    requirements: ["Foundation complete", "Quarterly audit passed", "Active license"],
  },
  {
    tier: "Master",
    status: "locked",
    description: "Full system access with governance participation rights",
    modules: "All modules + audit rights",
    requirements: ["Operator tier complete", "Revenue threshold met", "Board approval"],
  },
];

const whatCertificationIs = [
  "A structured progression through authority doctrine",
  "Locked modules that enforce completion order",
  "Timed examinations with auto-fail conditions",
  "Operating standards that are audited",
  "A prerequisite for license issuance",
];

const whatCertificationIsNot = [
  "Self-paced learning",
  "Optional coursework",
  "A credential without enforcement",
  "Something you can buy without approval",
  "A guarantee of license continuity",
];

const enforcementStandards = [
  {
    standard: "Module Completion",
    rule: "Cannot skip. Cannot fast-forward. Locked progression.",
  },
  {
    standard: "Exam Requirements",
    rule: "Minimum 80% pass rate. Time-limited. No retakes for 30 days.",
  },
  {
    standard: "Audit Compliance",
    rule: "Quarterly review. Failure triggers probation or revocation.",
  },
  {
    standard: "License Dependency",
    rule: "Certification enables license. License can be revoked independently.",
  },
];

const CertificationPage = () => {
  return (
    <div className="min-h-screen bg-[#080808]" data-testid="certification-page">
      <SEOHead 
        title="Certification - OnPoint Authority Systems"
        description="Authority certification: structured progression, locked modules, enforcement standards. This is not education. This is licensed authority."
        url="https://onpointauthority.com/certification"
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 md:pt-44 md:pb-24 px-6 md:px-12" data-testid="certification-hero">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Authority Certification
            </p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[0.95] text-foreground mb-8">
              Certification<br />
              <span className="text-primary">Standards</span>
            </h1>
            <div className="space-y-6 text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-2xl">
              <p className="text-foreground">
                Certification is not education.<br />
                It is a gate to licensed authority.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certification Structure */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="certification-structure">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              Certification Structure
            </p>
            <ul className="space-y-4">
              {[
                "Doctrine (what authority is)",
                "Procedure (how authority is exercised)",
                "Enforcement (what happens when it breaks)",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-lg text-foreground"
                >
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-3" />
                  {item}
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* What Certification Enables */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="certification-enables">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-8">
              What Certification Enables
            </p>
            <ul className="space-y-4 mb-10">
              {[
                "Eligibility for licensing",
                "Access to the authority portal",
                "Audit participation",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start gap-3 text-lg text-foreground"
                >
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-3" />
                  {item}
                </motion.li>
              ))}
            </ul>
            <p className="text-lg font-light text-muted-foreground italic">
              Licensing is granted, not purchased.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Certification Is / Is Not */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="what-certification-is">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* What It Is */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle className="h-6 w-6 text-primary" />
                <h2 className="font-serif text-3xl font-light text-foreground">What Certification Is</h2>
              </div>
              <ul className="space-y-4">
                {whatCertificationIs.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-foreground">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mt-2.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* What It Is Not */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="h-6 w-6 text-red-400" />
                <h2 className="font-serif text-3xl font-light text-foreground">What Certification Is Not</h2>
              </div>
              <ul className="space-y-4">
                {whatCertificationIsNot.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-muted-foreground">
                    <div className="w-1.5 h-1.5 bg-red-400 rounded-full mt-2.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Certification Levels */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="certification-levels">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Locked View
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Certification <span className="text-primary">Levels</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {certificationLevels.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/10 p-6 relative"
              >
                {/* Lock indicator */}
                <div className="absolute top-4 right-4">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <Award className={`h-6 w-6 ${index === 0 ? "text-amber-600" : index === 1 ? "text-gray-400" : "text-primary"}`} />
                  <h3 className="font-serif text-2xl text-foreground">{level.tier}</h3>
                </div>

                <p className="text-sm text-muted-foreground mb-4">{level.description}</p>

                <div className="mb-4">
                  <p className="text-xs text-muted-foreground uppercase mb-2">Access:</p>
                  <p className="text-sm font-mono text-primary">{level.modules}</p>
                </div>

                <div>
                  <p className="text-xs text-muted-foreground uppercase mb-2">Requirements:</p>
                  <ul className="space-y-1">
                    {level.requirements.map((req, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-foreground">
                        <div className="w-1 h-1 bg-primary rounded-full" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 text-center"
          >
            <p className="text-sm text-muted-foreground">
              Detailed pricing and module content are disclosed only after application approval.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Enforcement Standards */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-t border-white/5" data-testid="enforcement-standards">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-red-400 mb-4">
              Non-Negotiable
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Enforcement <span className="text-primary">Standards</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {enforcementStandards.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card/5 border border-white/10 p-6"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-mono text-primary">{item.standard}</h3>
                </div>
                <p className="text-foreground">{item.rule}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 bg-red-500/10 border border-red-500/20 p-6"
          >
            <div className="flex items-start gap-4">
              <AlertTriangle className="h-6 w-6 text-red-400 flex-shrink-0" />
              <div>
                <p className="text-foreground font-medium mb-2">Critical Notice</p>
                <p className="text-muted-foreground">
                  Certification completion does not guarantee license issuance or continuity. 
                  Licenses are subject to ongoing audit, enforcement review, and can be revoked 
                  independently of certification status.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Module Structure Preview */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#050505]" data-testid="module-structure">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-4">
              Each Module Contains
            </p>
            <h2 className="font-serif text-4xl font-light text-foreground">
              Module <span className="text-primary">Structure</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: BookOpen, title: "Doctrine", desc: "Authority rationale (non-skippable)" },
              { icon: Target, title: "Procedure", desc: "Operating standards and frameworks" },
              { icon: Scale, title: "Enforcement", desc: "What triggers violation and remediation" },
              { icon: Award, title: "Examination", desc: "Time-gated assessment with pass requirements" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-card/5 border border-white/10 p-5 text-center"
              >
                <item.icon className="h-8 w-8 text-primary mx-auto mb-3" strokeWidth={1.5} />
                <h3 className="text-sm font-medium text-foreground mb-2">{item.title}</h3>
                <p className="text-xs text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12" data-testid="certification-cta">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs font-medium tracking-[0.2em] uppercase text-muted-foreground mb-6">
              Access Is Selective
            </p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-6">
              Begin with<br />
              <span className="text-primary">Application</span>
            </h2>
            <p className="text-muted-foreground mb-10 max-w-xl mx-auto">
              Certification access is not open enrollment. Submit an application to 
              determine eligibility. Acceptance is based on fit, not payment.
            </p>
            <Link to="/operator-application">
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.15em] text-xs font-bold px-10 py-6 rounded-none gold-glow"
                data-testid="certification-apply-btn"
              >
                Submit Application
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CertificationPage;
